import { useLocation, useNavigate } from 'react-router-dom';
import { useGoBack } from '@/lib/goBack';
import { useKeptScroll } from '@/lib/keptScroll';
import { useAuth } from '@/lib/auth';
import { useChats } from '@/data/store';
import { channelPairs, onePerCustomer } from '@/lib/channels';
import { seesChat } from '@/lib/chatGroups';
import { BackIcon } from '@/components/Icons';
import { ChatRows, SelectionBar, ordered, useSelection } from '@/components/ChatListParts';

// Archived chats, reached from the row at the top of the chat list, as in WhatsApp.
// Archiving only hides a chat from the list: the record stays in Sentinel.

export function ArchivedScreen() {
  const listScroll = useKeptScroll<HTMLElement>('archived');
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { pathname } = useLocation();
  const { access, session } = useAuth();
  const { chats, keepArchived } = useChats();
  const selection = useSelection();
  const visible = chats.filter((c) => seesChat(c, access, session?.user.id));
  // Closed, junked or quiet: nothing to read here (Gary, 19 Oct: junked emails showed green counts).
  // One row per customer, as in Chats, so Unarchive brings back their WhatsApp and email together (regression run, 19 Oct).
  const inArchive = visible.filter((c) => c.archived).map((c) => ({ ...c, unread: 0, markedUnread: false }));
  const archived = ordered(onePerCustomer(inArchive, channelPairs(inArchive)));

  // Back steps back through history, so the phone's Back does not return here (mobile review, 19 Oct). On a
  // computer with a chat open beside Archived it opens the chat list, as before.
  const back = () => { if (pathname === '/archived') goBack('/'); else navigate('/'); };

  return (
    <>
      {selection.selecting ? (
        <SelectionBar selection={selection} chats={archived} shown={archived} />
      ) : (
        <header className="chats-topbar chats-topbar--conv">
          <button type="button" className="chats-icon-btn" aria-label="Back" onClick={back}><BackIcon /></button>
          <h1 className="chats-topbar-title">Archived</h1>
        </header>
      )}
      <main ref={listScroll} className="chats-screen">
        <p className="chats-info">
          Closed and junked chats, and chats quiet for 7 days. A new message from the customer starts a fresh chat
          {keepArchived ? '. ' : ', and a quiet chat comes back to your list. '}
          <button type="button" className="chats-link" onClick={() => navigate('/settings')}>Archive settings</button>
        </p>
        {archived.length === 0 && <p className="chats-empty">No archived chats.</p>}
        {/* An older closed chat beside the customer's current one says Closed (regression run, 19 Oct). */}
        <ChatRows chats={archived} selection={selection} showBrand markOld="closed" />
      </main>
    </>
  );
}
